import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listRules,
  publishConfig,
  updateRule,
  type SafegeoRule,
} from "../lib/safegeo-api";
import { actionLabel, countryLabel } from "../lib/format";
import { queryKeys } from "../lib/query-keys";
import { AppButton } from "./AppButton";
import { AppLink } from "./AppLink";
import { ErrorBanner } from "./PageBanner";

type Props = {
  atRulesMax: boolean;
  rulesMaxHint?: string;
};

export function WebsiteRulesSection({ atRulesMax }: Props) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const rulesQ = useQuery({ queryKey: queryKeys.rules, queryFn: listRules });
  const websiteRules = (rulesQ.data?.items ?? []).filter(
    (r) => !r.is_fallback && r.action !== "block",
  );

  const afterMutate = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => {
      await fn();
      try {
        await publishConfig();
      } catch {
        /* ok */
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["safegeo"] }),
  });

  async function toggle(id: string, enabled: boolean) {
    setBusy(true);
    try {
      await afterMutate.mutateAsync(() =>
        updateRule(id, { is_enabled: enabled }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <s-section
      heading="Websites"
      subheading="Send shoppers from chosen countries to another website you own, including another Shopify shop that isn’t connected here."
    >
      <s-badge tone="info">Starter+</s-badge>
      {rulesQ.isLoading ? (
        <s-paragraph color="subdued">Loading…</s-paragraph>
      ) : null}
      {rulesQ.error ? (
        <ErrorBanner heading="Couldn’t load rules">
          {(rulesQ.error as Error).message}
        </ErrorBanner>
      ) : null}
      <WebsiteTable
        rules={websiteRules}
        busy={busy}
        atRulesMax={atRulesMax}
        onToggle={(id, on) => void toggle(id, on)}
      />
    </s-section>
  );
}

function WebsiteTable({
  rules,
  busy,
  atRulesMax,
  onToggle,
}: {
  rules: SafegeoRule[];
  busy: boolean;
  atRulesMax: boolean;
  onToggle: (id: string, enabled: boolean) => void;
}) {
  if (rules.length === 0) {
    return (
      <s-box padding="base" borderWidth="base" borderRadius="base">
        <s-stack direction="block" gap="base">
          <s-text type="strong">No websites yet</s-text>
          <s-paragraph color="subdued">
            Send United Kingdom visitors to your .co.uk site, or any website you
            own.
          </s-paragraph>
          <AppButton
            variant="primary"
            to={atRulesMax ? "/app/billing" : "/app/stores/new"}
          >
            {atRulesMax ? "Rule limit reached" : "Add rule"}
          </AppButton>
        </s-stack>
      </s-box>
    );
  }

  return (
    <s-table>
      <s-table-header-row>
        <s-table-header listSlot="primary">Name / countries</s-table-header>
        <s-table-header>Destination</s-table-header>
        <s-table-header>Behavior</s-table-header>
        <s-table-header>Status</s-table-header>
        <s-table-header>Actions</s-table-header>
      </s-table-header-row>
      <s-table-body>
        {rules.map((rule) => (
          <s-table-row key={rule.id}>
            <s-table-cell>
              <AppLink to={`/app/stores/${rule.id}`}>
                {rule.countries?.map(countryLabel).join(", ") || "-"}
              </AppLink>
            </s-table-cell>
            <s-table-cell>{rule.destination_url || "—"}</s-table-cell>
            <s-table-cell>
              {actionLabel(rule.action)}
              {rule.keep_path === false
                ? " · home page"
                : rule.keep_path
                  ? " · keep path"
                  : " · home page"}
            </s-table-cell>
            <s-table-cell>
              <s-badge tone={rule.is_enabled ? "success" : "info"}>
                {rule.is_enabled ? "Active" : "Off"}
              </s-badge>
            </s-table-cell>
            <s-table-cell>
              <s-stack direction="inline" gap="small">
                <AppButton variant="tertiary" to={`/app/stores/${rule.id}`}>
                  Edit
                </AppButton>
                <s-button
                  variant="tertiary"
                  disabled={busy}
                  onClick={() => onToggle(rule.id, !rule.is_enabled)}
                >
                  {rule.is_enabled ? "Turn off" : "Turn on"}
                </s-button>
              </s-stack>
            </s-table-cell>
          </s-table-row>
        ))}
      </s-table-body>
    </s-table>
  );
}
